import _extends from "@babel/runtime/helpers/extends";
import PropTypes from 'prop-types';
import React from 'react';
import ChevronRight from 'wix-ui-icons-common/ChevronRight';
import More from 'wix-ui-icons-common/More';
import PopoverMenu from '../PopoverMenu';
import Button from '../Button';
import IconButton from '../IconButton';
import Tooltip from '../Tooltip';
import { dataHooks } from './constants';
import HoverSlot from './HoverSlot';
import style from './TableActionCell.st.css';
/* eslint-disable react/prop-types */

function renderPrimaryAction(_ref) {
  var text = _ref.text,
      skin = _ref.skin,
      _onClick = _ref.onClick,
      disabled = _ref.disabled;
  return /*#__PURE__*/React.createElement(Button, {
    disabled: disabled,
    skin: skin,
    onClick: function onClick(event) {
      _onClick(); // Making sure we don't also trigger onRowClick


      event.stopPropagation();
    }
  }, text);
}
/* eslint-enable react/prop-types */


function renderVisibleActions(actions) {
  return actions.map(function (_ref2, index) {
    var text = _ref2.text,
        icon = _ref2.icon,
        _onClick2 = _ref2.onClick,
        dataHook = _ref2.dataHook,
        disabled = _ref2.disabled,
        disabledDescription = _ref2.disabledDescription;
    return /*#__PURE__*/React.createElement(Tooltip, {
      key: index,
      dataHook: dataHook || dataHooks.tableActionCellVisibleActionTooltip,
      content: disabled && Boolean(disabledDescription) ? disabledDescription : text
    }, /*#__PURE__*/React.createElement(IconButton, {
      skin: "inverted",
      disabled: disabled,
      onClick: function onClick(event) {
        _onClick2();

        event.stopPropagation();
      }
    }, icon));
  });
}

function renderHiddenActions(actions, popoverMenuProps) {
  return /*#__PURE__*/React.createElement(PopoverMenu, _extends({
    dataHook: dataHooks.tableActionCellPopoverMenu,
    appendTo: "parent",
    placement: "top",
    textSize: "small",
    triggerElement: /*#__PURE__*/React.createElement(IconButton, {
      skin: "inverted",
      dataHook: dataHooks.triggerElement
    }, /*#__PURE__*/React.createElement(More, null))
  }, popoverMenuProps), actions.map(function (_ref3, index) {
    var text = _ref3.text,
        icon = _ref3.icon,
        _onClick3 = _ref3.onClick,
        disabled = _ref3.disabled,
        dataHook = _ref3.dataHook,
        divider = _ref3.divider;
    return !divider ? /*#__PURE__*/React.createElement(PopoverMenu.MenuItem, {
      key: index,
      dataHook: dataHook || dataHooks.tableActionCellPopoverMenuItem,
      prefixIcon: icon,
      onClick: function onClick() {
        return _onClick3();
      },
      text: text,
      disabled: disabled
    }) : /*#__PURE__*/React.createElement(PopoverMenu.Divider, null);
  }));
}

function renderPlaceholder() {
  return /*#__PURE__*/React.createElement(IconButton, {
    skin: "inverted"
  }, /*#__PURE__*/React.createElement(ChevronRight, null));
}

var TableActionCell = function TableActionCell(props) {
  var dataHook = props.dataHook,
      primaryAction = props.primaryAction,
      secondaryActions = props.secondaryActions,
      numOfVisibleSecondaryActions = props.numOfVisibleSecondaryActions,
      alwaysShowSecondaryActions = props.alwaysShowSecondaryActions,
      popoverMenuProps = props.popoverMenuProps;
  var visibleActions = secondaryActions.slice(0, numOfVisibleSecondaryActions);
  var hiddenActions = secondaryActions.slice(numOfVisibleSecondaryActions);
  return /*#__PURE__*/React.createElement("span", _extends({
    "data-hook": dataHook
  }, style('root', {}, props)), primaryAction && /*#__PURE__*/React.createElement(HoverSlot, {
    display: "onHover",
    "data-hook": dataHooks.tableActionCellPrimaryAction
  }, renderPrimaryAction(primaryAction)), visibleActions.length > 0 && /*#__PURE__*/React.createElement(HoverSlot, {
    display: alwaysShowSecondaryActions ? 'always' : 'onHover',
    "data-hook": dataHooks.tableActionCellVisibleActions
  }, renderVisibleActions(visibleActions)), hiddenActions.length > 0 && /*#__PURE__*/React.createElement("div", {
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    className: style.popoverMenu
  }, /*#__PURE__*/React.createElement(HoverSlot, {
    display: "always"
  }, renderHiddenActions(hiddenActions, popoverMenuProps))), primaryAction && !(secondaryActions || []).length && /*#__PURE__*/React.createElement(HoverSlot, {
    display: "notOnHover",
    className: style.placeholderIcon,
    "data-hook": dataHooks.tableActionCellPlaceholder
  }, renderPlaceholder()));
};

TableActionCell.displayName = 'TableActionCell';
TableActionCell.propTypes = {
  dataHook: PropTypes.string,

  /**
   * An object containing the primary action properties: `text` is the action
   * text , `theme` is the button theme (can be `whiteblue` or `fullblue`),
   * `onClick` is the callback function for the action, whose signature is
   * `onClick(rowData, rowNum)`.
   * `disabled` is an optional prop for the primary action to be disabled
   */
  primaryAction: PropTypes.shape({
    text: PropTypes.string.isRequired,
    skin: PropTypes.oneOf(['standard', 'inverted']),
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool
  }),

  /**
   * An array containing the secondary actions: `text` is the action text
   * (will be shown in the tooltip), `icon` is the icon component for the
   * action, `onClick` is the callback function for the action, whose
   * signature is `onClick(rowData, rowNum)`.
   * `disabled` is an optional prop for the secondary action to be disabled
   * `dataHook` is an optional prop for accessing the action in tests
   * 'disabledDescription' is an optional prop that indicates what string to display in tooltip when action is visible and disabled (if non is provided, the text prop is used)
   * 'divider' is an optional prop to display a divider between the action items
   */
  secondaryActions: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    dataHook: PropTypes.string,
    disabledDescription: PropTypes.string,
    divider: PropTypes.bool
  })),

  /** The number of secondary actions to show outside the PopoverMenu */
  numOfVisibleSecondaryActions: PropTypes.number,

  /** Whether to show the secondary action also when not hovering the row */
  alwaysShowSecondaryActions: PropTypes.bool,

  /** Props being passed to the secondary actions' <PopoverMenu/> */
  popoverMenuProps: PropTypes.shape(PopoverMenu.propTypes)
};
TableActionCell.defaultProps = {
  primaryAction: null,
  secondaryActions: [],
  numOfVisibleSecondaryActions: 0,
  alwaysShowSecondaryActions: false
};
export default TableActionCell;